/**
 * Forgetting a password, and setting a new one.
 *
 * Two screens that share a file because they are two halves of one errand.
 *
 * The thing both of them do, and the reason the copy reads the way it does:
 * **they never say whether an account exists.** The confirmation is the same
 * sentence whether the address matched somebody or nobody, and it is written
 * so that it is honest either way — "if that matches an account" rather than
 * "we have sent you an email". Anything else turns this form into a way to
 * find out who trains at Qatar Care.
 */
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { AuthShell } from '../Brand';

export function ForgotPassword() {
  const [identifier, setIdentifier] = useState('');
  const [sent, setSent] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post<{ message: string }>('/auth/forgot', { identifier: identifier.trim() });
      setSent(res.message);
    } catch {
      // Even a failure says the same thing: the difference between "sent" and
      // "something went wrong" is the same leak by another route.
      setSent('If that matches an account with an email address, a link is on its way.');
    } finally { setBusy(false); }
  };

  return (
    <AuthShell>
      <div className="gate__card">
        <h1 className="gate__title">Get a password link</h1>
        <p className="gate__sub">
          For your first password here, or if you have forgotten yours. The link goes to the email
          address on your account.
        </p>

        {sent ? (
          <>
            <p className="notice">{sent}</p>
            <p className="muted small">
              Check your junk folder if it does not arrive. If you share an email address with
              somebody else on the programme, each account gets its own link and each one says
              which username it is for.
            </p>
            <Link className="btn btn--ghost" to="/">Back to sign in</Link>
          </>
        ) : (
          <form onSubmit={submit}>
            <label htmlFor="identifier">Username or email</label>
            <input
              id="identifier" value={identifier} autoFocus required autoComplete="username"
              onChange={(e) => setIdentifier(e.target.value)}
            />
            <button className="btn btn--primary" disabled={busy || !identifier.trim()}>
              {busy ? 'Sending…' : 'Send me a link'}
            </button>
            <Link className="gate__link" to="/">Back to sign in</Link>
          </form>
        )}
      </div>
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const mismatch = again !== '' && password !== again;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mismatch) return;
    setBusy(true); setError(null);
    try {
      await api.post('/auth/reset', { token, newPassword: password });
      setDone(true);
    } catch (err) { setError((err as ApiError).message); }
    finally { setBusy(false); }
  };

  if (!token) {
    return (
      <AuthShell>
        <div className="gate__card">
          <h1 className="gate__title">This link is incomplete</h1>
          <p className="gate__sub">Part of the link is missing. This can happen when a link is copied by hand.</p>
          <Link className="btn btn--primary" to="/forgot-password">Ask for a new link</Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className="gate__card">
        <h1 className="gate__title">Choose your password</h1>

        {done ? (
          <>
            <p className="notice">Your password is set. Sign in with it.</p>
            {/* Not signed in automatically: holding the email is not the same
                as knowing the password, and if the link reached the wrong
                person this is one more thing they do not have. */}
            <button className="btn btn--primary" onClick={() => navigate('/')}>Sign in</button>
          </>
        ) : (
          <form onSubmit={submit}>
            <label htmlFor="pw">New password</label>
            <input
              id="pw" type="password" value={password} autoFocus required autoComplete="new-password"
              onChange={(e) => setPassword(e.target.value)}
            />

            <label htmlFor="pw2">Type it again</label>
            <input
              id="pw2" type="password" value={again} required autoComplete="new-password"
              onChange={(e) => setAgain(e.target.value)}
            />
            {mismatch && <p className="warn small">Those two do not match.</p>}

            {error && <p className="auth__error" role="alert">{error}</p>}

            <button className="btn btn--primary" disabled={busy || mismatch || !password}>
              {busy ? 'Setting…' : 'Set my password'}
            </button>
            <Link className="gate__link" to="/forgot-password">Ask for a new link</Link>
          </form>
        )}
      </div>
    </AuthShell>
  );
}
